import React, { useState } from "react";

const EncodingQualityIcon = ({ avgQp, size = 24, position, codec }) => {
  const [hovered, setHovered] = useState(false);

  // QP is the encoder's quantization parameter: LOW is crisp, HIGH is heavily
  // compressed. avgQp arrives already normalized to the H.264 0-51 scale (see
  // normalizeQpToH264Scale in App.js — AV1's 0-255 qindex is mapped down), so
  // one set of bands serves every codec. Bands match Epic's VideoQpIndicator
  // (orangeQP=26, redQP=35) — this was inverted here once, showing "Blocky"
  // on excellent streams.
  //
  // QP is only reported by the NVENC H264/H265/AV1 encoders. VP8/VP9 stream from
  // Unreal's SOFTWARE (libvpx) encoders, which never report avgQP — so avgQp stays
  // at its initial -1 for the whole session. We must NOT (a) render it invisibly
  // (the old "black" on the dark bar looked like a missing icon) or (b) claim
  // "Not connected" on a perfectly good VP8/VP9 stream.
  const qpUnavailable = ['VP8', 'VP9'].includes((codec || '').toUpperCase());
  let color = "green";
  let hoverText = "Clear encoding quality";

  if (avgQp <= 0) {
    // Muted grey (NOT black) so the icon is visible on the dark control bar; the
    // tooltip is honest — VP8/VP9 have no QP metric, everything else is just
    // still connecting before the first QP sample arrives.
    color = "#8b95a5";
    hoverText = qpUnavailable
      ? `Encoding-quality metric isn't reported for ${(codec || '').toUpperCase()}`
      : "Connecting…";
  } else if (avgQp > 35) {
    color = "red";
    hoverText = "Poor encoding quality";
  } else if (avgQp > 26) {
    color = "orange";
    hoverText = "Blocky encoding quality";
  }

  const tooltipTextColor = color === "black" ? "white" : color;

  // Signal-strength metaphor: more bars = better picture (= lower QP).
  let filledBars = 0;
  if (avgQp > 0) {
    if (avgQp <= 20) filledBars = 4;
    else if (avgQp <= 26) filledBars = 3;
    else if (avgQp <= 35) filledBars = 2;
    else filledBars = 1;
  }

  const barsData = [
    { x: 3, y: 14, h: 7 },
    { x: 8, y: 11, h: 10 },
    { x: 13, y: 8, h: 13 },
    { x: 18, y: 5, h: 16 },
  ];

   const tooltipStyle = {
  position: "absolute",
  bottom: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.85)",
  borderRadius: 8,
  padding: "12px",
  fontSize: 12,
  fontWeight: 500,
  color: tooltipTextColor,
  textAlign: "center",
  whiteSpace: "nowrap",
  zIndex: 1000,
  marginBottom: 12,
  ...(position === "Left"
    ? { right: -10, transform: "translateX(0)" }     
    : { left: -10, transform: "translateX(0)" }),   
};
 return (
    <div
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 20 20"
        role="img"
        aria-label={hoverText}
      >
        {barsData.map((bar, index) => (
          <rect
            key={index}
            x={bar.x}
            y={bar.y}
            width="3"
            height={bar.h}
            rx="1"
            fill={color}
            opacity={index < filledBars ? 1 : 0.25}
            style={{ transition: "opacity 0.2s ease" }}
          />
        ))}
      </svg>

      {hovered && <div style={tooltipStyle}>{hoverText}</div>}
    </div>
  );
};

export default EncodingQualityIcon;